import { Alert, Button, Group, Select, Stack, Text, TextInput } from '@mantine/core';
import { IconAlertTriangle, IconExternalLink } from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  EDIT_MODE,
  SEARCH_EDIT_MODE,
  buildEditUrl,
  buildPreviewUrl,
  editablePagesOf,
  originOf,
  withPreviewMode,
} from '../../helpers/previewSdk';
import transformWizardStyles from '../../helpers/transformWizardStyles';
import useFetch from '../../helpers/useFetch';

// Keystrokes should not each cost a write to the draft the shop page polls.
const DRAFT_DEBOUNCE_MS = 400;

// How often to ask what the page did with the draft. The page polls on its own
// clock too, so its report lands a beat after the edit either way.
const STATUS_POLL_MS = 2000;

const DRAFT_COPY = {
  pending: 'sending the draft',
  failed: 'the draft did not send',
  invalid: 'this layout could not be built for your page',
};

// Zero blocks means different things per scope. A web layout is placed at a
// selector, so nothing rendered means the page had nowhere to put it. A search
// layout only renders once someone searches, so the page acks zero until they
// do — which is not a failure to report as one.
const NO_BLOCKS_COPY = {
  web: 'nowhere to put it on that page',
  search: 'nothing searched on that page yet',
};

const POPUP_BLOCKED = 'Your browser blocked the new window. Allow pop-ups for this page and click again.';

const NO_SEARCH_URL = 'That is not a web address we can open.';

// A failed /search lookup (5xx, a dropped connection, anything `useFetch`
// rejects on) says nothing about whether the domain actually has search set
// up — it is not the same as a resolved "no config" answer, and must not be
// asserted as one. This sentinel keeps the two apart in state so the guard
// below can let the button through on a failed check instead of pinning the
// user on a dead end with no retry.
const SEARCH_CONFIG_UNKNOWN = 'unknown';

// What the shop page should render: the same body the preview compiles, so the
// page shows what saving would produce rather than the pre-wizard markup.
const draftBodyOf = (template) => (template.advancedEditor ? template.body : transformWizardStyles(template));

// What we can honestly say about the last change. Only the page knows whether a
// draft became anything, so an ack for an older version — or none at all — means
// we do not know yet, which is not the same as nothing having rendered.
const draftStatusText = (draft, applied, scope) => {
  if (draft.status !== 'sent') {
    return DRAFT_COPY[draft.status];
  }

  if (!applied) {
    return 'waiting for your page to pick it up';
  }

  if (!Number.isFinite(applied.blocks)) {
    return 'your page picked it up';
  }

  if (applied.blocks === 0) {
    return NO_BLOCKS_COPY[scope] || NO_BLOCKS_COPY.web;
  }

  return `rendered on ${applied.blocks} block${applied.blocks === 1 ? '' : 's'}`;
};

// Drives this layout onto the customer's real site without framing it: they keep
// editing here, a page opens in a second window, and the SDK there polls for the
// draft this writes. Unlike a framed preview it works whatever the shop's
// x-frame-options says, because nothing is embedded.
//
// A web layout is driven on one of the pages it is placed on. A search layout
// has no placement — it is attached through the domain's search config and shows
// up wherever the shop's search box is — so it is driven on a url instead, in a
// mode of its own.
const EditOnMySite = ({ template }) => {
  const { domain } = useSelector((state) => state.domain);
  const fetch = useFetch();
  const isSearch = template.scope === 'search';
  const mode = isSearch ? SEARCH_EDIT_MODE : EDIT_MODE;
  const [pageId, setPageId] = useState(null);
  // Where a search session opens. Any page carrying the search box will do, so
  // this is a url to correct rather than a page to pick.
  const [siteUrl, setSiteUrl] = useState(domain.url || '');
  // Sent along so the page opens on a populated dropdown instead of needing
  // something typed into the search box before there is anything to look at.
  const [seedQuery, setSeedQuery] = useState('');
  const [session, setSession] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState({ status: 'pending' });
  const [ack, setAck] = useState(null);
  // Guards against two in-flight draft PUTs resolving out of order: a slow
  // earlier write must not overwrite the status line after a faster later one
  // has already landed. Bumped right before each write actually fires, so it
  // orders writes rather than render-triggering body changes.
  const draftSeq = useRef(0);
  // The domain's search-as-you-type config, for search layouts only. `undefined`
  // while it is being read, `null` when the domain has none. Without a config
  // that points at this layout the SDK never attaches to anything, so the page
  // would open and simply never show the dropdown.
  const [searchConfig, setSearchConfig] = useState(undefined);

  const domainPath = `/api/admin/accounts/${domain.accountId}/domains/${domain.id}`;
  const sessionsPath = `${domainPath}/templates/${template.id}/edit-sessions`;

  useEffect(() => {
    if (!isSearch) {
      return;
    }

    fetch(`${domainPath}/search`)
      .then(({ search }) => setSearchConfig(search || null))
      .catch(() => setSearchConfig(SEARCH_CONFIG_UNKNOWN));
  }, [domainPath, fetch, isSearch]);

  const pages = editablePagesOf(template);
  const page = pages.find((option) => String(option.pageId) === pageId) || pages[0];

  // Only computed while a session is live, so an idle editor doesn't re-run the
  // wizard transform on every keystroke. A body the transform cannot build is
  // not a reason to take the editor down — `null` means "nothing to send",
  // and the status line says so instead of throwing through `TemplateForm`.
  const body = useMemo(() => {
    try {
      return session ? draftBodyOf(template) : null;
    } catch {
      return null;
    }
  }, [session, template]);

  useEffect(() => {
    if (!session) {
      return;
    }

    if (body === null) {
      // Invalidates any write already in flight, so its response can't land
      // afterwards and claim the layout sent when it never built.
      draftSeq.current += 1;
      setDraft({ status: 'invalid' });
      return;
    }

    setDraft({ status: 'pending' });

    const timer = window.setTimeout(() => {
      // Captured when the write actually fires (after the debounce), so a
      // write superseded by a newer one before it resolves is dropped instead
      // of overwriting the status line with stale markup's version.
      const mine = ++draftSeq.current;

      // The version the write returns is what the page's ack is matched against,
      // so the line below can tell this change apart from the one before it.
      fetch(`${sessionsPath}/${session.token}/draft`, { method: 'PUT', body: JSON.stringify({ body }) })
        .then(({ version }) => {
          if (mine === draftSeq.current) {
            setDraft({ status: 'sent', version });
          }
        })
        .catch(() => {
          if (mine === draftSeq.current) {
            setDraft({ status: 'failed' });
          }
        });
    }, DRAFT_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [body, fetch, session, sessionsPath]);

  // The page's own account of what it did with the draft. Nothing is framed, so
  // there is no postMessage channel back — the SDK leaves the ack on the session
  // instead. A poll that fails keeps the last ack, which reads as still waiting.
  useEffect(() => {
    if (!session) {
      return;
    }

    setAck(null);

    const timer = window.setInterval(() => {
      fetch(`${sessionsPath}/${session.token}/status`)
        .then((status) => setAck(status.ack || null))
        .catch(() => {});
    }, STATUS_POLL_MS);

    return () => window.clearInterval(timer);
  }, [fetch, session, sessionsPath]);

  // One cleanup covers all three ways a session ends: the End button clears it,
  // starting one for another page replaces it, and unmounting drops it. A failed
  // delete needs no handling here — the session carries its own expiry.
  useEffect(() => {
    if (!session) {
      return;
    }

    return () => {
      fetch(`${sessionsPath}/${session.token}`, { method: 'DELETE' }).catch(() => {});
    };
  }, [fetch, session, sessionsPath]);

  const start = async () => {
    setError(null);

    // Opened before any await, or the popup blocker takes it — a window.open
    // that is not a direct consequence of the click is not user-initiated. It
    // sits on about:blank until the token exists.
    const shopWindow = window.open('', '_blank');

    if (!shopWindow) {
      setError(POPUP_BLOCKED);
      return;
    }

    // Disowned while it is still about:blank and same-origin: once it is on the
    // shop's page, any script there could otherwise navigate this tab through
    // `window.opener.location` ('noopener' isn't usable here since the design
    // needs the handle to set `.location` once the token exists).
    shopWindow.opener = null;

    setStarting(true);

    try {
      let openUrl;
      let label;

      if (isSearch) {
        // No placement to resolve and no page id to look one up by: the url is
        // whatever page of the site the user wants to search on.
        openUrl = withPreviewMode(siteUrl, mode);
        label = originOf(siteUrl);

        if (!openUrl) {
          throw new Error(NO_SEARCH_URL);
        }
      } else {
        // A page's pathname can be a pattern rather than a reachable url, so ask
        // for the most recent url actually seen on it; when there is none (or the
        // lookup fails) the pathname against the domain is the best guess left.
        const { url } = await fetch(`${domainPath}/pages/${page.pageId}/recent-url`).catch(() => ({}));

        openUrl = withPreviewMode(url, mode) || buildPreviewUrl(domain.url, page.pathname, mode);
        label = page.name;

        if (!openUrl) {
          throw new Error(`There is no web address we can open for ${page.name}.`);
        }
      }

      const { token } = await fetch(sessionsPath, { method: 'POST' });

      shopWindow.location = buildEditUrl(openUrl, token, { mode, query: seedQuery.trim() });
      setSession({ token, pageName: label });
    } catch (e) {
      shopWindow.close();
      setError(e.message || 'Could not start a session on your site.');
    } finally {
      setStarting(false);
    }
  };

  if (!isSearch && template.scope !== 'web') {
    return null;
  }

  // template.id is what sessionsPath is built from. A layout that has never
  // been saved (or a web layout duplicated from a placed one, which keeps
  // recommenders but not the id) has no id yet — reachable in the search
  // scope because it has no pages.length guard to incidentally catch it.
  if (!template.id) {
    return (
      <Text size="xs" c="dimmed" mb="xs">
        Save this layout first, and you can then edit it on your own site.
      </Text>
    );
  }

  if (!isSearch && !pages.length) {
    return (
      <Text size="xs" c="dimmed" mb="xs">
        This layout is not used on any page yet, so there is no page of your site to edit it on.
      </Text>
    );
  }

  // Nothing to attach to, or attached to a different layout: either way the
  // dropdown this edits never appears on the page, and opening a window that
  // stays empty is worse than saying why. A lookup that failed rather than
  // resolved is neither of those — it is not information about the domain's
  // config — so it falls through to the button below instead of asserting a
  // state we never actually read.
  if (isSearch && searchConfig !== undefined && searchConfig !== SEARCH_CONFIG_UNKNOWN) {
    if (!searchConfig || !searchConfig.cssSelector) {
      return (
        <Text size="xs" c="dimmed" mb="xs">
          Your site&apos;s search-as-you-type is not set up yet, so there is no search box to open this layout in.
        </Text>
      );
    }

    // Written and read as a string throughout the rest of the admin
    // (`SearchAsYouTypePage.jsx` PUTs the Select's string value verbatim), so
    // comparing raw would read a correctly configured layout as a different
    // one the moment the api echoes it back as a string.
    if (String(searchConfig.templateId) !== String(template.id)) {
      return (
        <Text size="xs" c="dimmed" mb="xs">
          Your site&apos;s search is using a different layout, so this one will not appear on the page yet.
        </Text>
      );
    }
  }

  // An ack only speaks for the version it names: an older one says nothing about
  // the change just sent, so it is not treated as a report on it.
  const applied = ack && draft.status === 'sent' && ack.version === draft.version ? ack : null;

  return (
    <Stack gap="xs" mb="xs">
      <Group gap="xs" wrap="nowrap">
        {session ? (
          <>
            <Text size="xs" c="dimmed" truncate>
              Editing on <strong>{session.pageName}</strong> — {draftStatusText(draft, applied, template.scope)}
            </Text>
            <Button size="compact-xs" variant="default" onClick={() => setSession(null)}>
              End session
            </Button>
          </>
        ) : (
          <>
            {isSearch ? (
              <>
                <TextInput
                  size="xs"
                  w={260}
                  aria-label="Page to search on"
                  placeholder="https://yourawesomewebsite.com"
                  value={siteUrl}
                  onChange={(e) => setSiteUrl(e.target.value)}
                />
                <TextInput
                  size="xs"
                  w={160}
                  aria-label="Sample query"
                  placeholder="Sample query"
                  value={seedQuery}
                  onChange={(e) => setSeedQuery(e.target.value)}
                />
              </>
            ) : (
              pages.length > 1 && (
                <Select
                  size="xs"
                  w={260}
                  aria-label="Page to edit on"
                  value={String(page.pageId)}
                  onChange={setPageId}
                  data={pages.map((option) => ({ value: String(option.pageId), label: option.name }))}
                />
              )
            )}
            <Button size="compact-xs" variant="default" loading={starting} onClick={start}>
              <IconExternalLink size={13} />
              &nbsp;Edit on my site
            </Button>
          </>
        )}
      </Group>

      {/* Beside the rendered count, not instead of it: the block did go in, and
          it is the layout's own script that failed once it was there. */}
      {applied?.scriptError && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />} p="xs">
          <Text size="xs">This layout&apos;s own script failed on your page: {applied.scriptError}</Text>
        </Alert>
      )}

      {error && (
        <Alert color="orange" icon={<IconAlertTriangle size={16} />} p="xs">
          <Text size="xs">{error}</Text>
        </Alert>
      )}
    </Stack>
  );
};

export default EditOnMySite;
